import { createClient, type User } from "@supabase/supabase-js";
import type { PortalConfig } from "../lib/config.js";

export const ACTIVATION_STORAGE_KEY = "ohf-staff-activation";

export type ActivationCallback =
  | { kind: "session"; accessToken: string; refreshToken: string; type: "invite" | "recovery" }
  | { kind: "otp"; tokenHash: string; type: "invite" | "recovery" };

export interface ActivationAuthClient {
  auth: {
    setSession(tokens: { access_token: string; refresh_token: string }): Promise<{ data: { session: { access_token: string } | null }; error: { message?: string } | null }>;
    verifyOtp(input: { token_hash: string; type: "invite" | "recovery" }): Promise<{ data: { session: { access_token: string } | null }; error: { message?: string } | null }>;
    getUser(): Promise<{ data: { user: User | null }; error: { message?: string } | null }>;
    updateUser(input: { password: string }): Promise<{ data: { user: User | null }; error: { message?: string } | null }>;
    signOut(options: { scope: "local" }): Promise<unknown>;
  };
}

export interface ActivationIdentity {
  email: string;
  displayName?: string;
}

export type ActivationResult =
  | { kind: "ready"; identity: ActivationIdentity }
  | { kind: "invalid"; reason: "missing" | "malformed" | "expired" | "blocked" | "subject" | "unavailable" };
export type ActivationInvalidReason = Extract<ActivationResult, { kind: "invalid" }>["reason"];

const allowedTypes = new Set(["invite", "recovery"]);
const callbackKeys = ["type", "token_hash", "access_token", "refresh_token", "error", "error_code", "error_description", "code", "token"];

export type ActivationLandingState = { kind: "confirmation" } | { kind: "loading" } | Extract<ActivationResult, { kind: "invalid" }>;

/** Classify only allowlisted provider codes; never retain or display descriptions. */
export function activationCallbackError(url: URL): ActivationInvalidReason {
  const fragment = new URLSearchParams(url.hash.slice(1));
  const query = url.searchParams;
  if (!url.search && !url.hash) return "missing";
  const duplicate = callbackKeys.some(key => fragment.getAll(key).length + query.getAll(key).length > 1);
  const credentials = ["token_hash", "access_token", "refresh_token", "type", "code", "token"].some(key => fragment.has(key) || query.has(key));
  const splitErrors = ["error", "error_code", "error_description"].some(key => fragment.has(key))
    && ["error", "error_code", "error_description"].some(key => query.has(key));
  if (!duplicate && !credentials && !splitErrors && (fragment.get("error_code") ?? query.get("error_code")) === "otp_expired") return "expired";
  return "malformed";
}

export function consumeActivationCallback(url: URL, scrub: () => void): ActivationCallback | null {
  const fragment = new URLSearchParams(url.hash.startsWith("#") ? url.hash.slice(1) : url.hash);
  const query = url.searchParams;
  let result: ActivationCallback | null = null;
  const duplicated = callbackKeys.some(key => fragment.getAll(key).length + query.getAll(key).length > 1);
  const queryAuth = callbackKeys.some(key => query.has(key));
  const fragmentAuth = callbackKeys.some(key => fragment.has(key));
  const source = queryAuth ? query : fragment;
  const type = source.get("type");
  const accessToken = source.get("access_token");
  const refreshToken = source.get("refresh_token");
  const tokenHash = source.get("token_hash");
  const forbidden = ["error", "error_code", "error_description", "code", "token"].some(key => fragment.has(key) || query.has(key));
  const validValue = (value: string | null): value is string => Boolean(value && value.length <= 16_384 && !/\s/.test(value));

  if (!duplicated && !(queryAuth && fragmentAuth) && !forbidden && type && allowedTypes.has(type)) {
    if (!queryAuth && validValue(accessToken) && validValue(refreshToken) && !source.has("token_hash")) {
      result = { kind: "session", accessToken, refreshToken, type: type as "invite" | "recovery" };
    } else if (validValue(tokenHash) && !source.has("access_token") && !source.has("refresh_token")) {
      result = { kind: "otp", tokenHash, type: type as "invite" | "recovery" };
    }
  }

  // Remove every query and fragment value, including malformed callbacks, before
  // any asynchronous auth or API operation can preserve it in history/logging.
  scrub();
  return result;
}

export function createActivationClient(config: PortalConfig): ActivationAuthClient | null {
  if (config.authMode !== "jwt" || !config.supabaseUrl || !config.supabasePublishableKey) return null;
  return createClient(config.supabaseUrl, config.supabasePublishableKey, {
    auth: {
      persistSession: false,
      autoRefreshToken: false,
      detectSessionInUrl: false,
      storageKey: ACTIVATION_STORAGE_KEY,
    },
  }) as unknown as ActivationAuthClient;
}

function validUser(user: User | null): user is User & { email: string } {
  return Boolean(user?.id && user.email && user.email_confirmed_at && !user.is_anonymous);
}

function deniedReason(status: number): ActivationInvalidReason {
  if (status === 401) return "expired";
  if (status === 403) return "blocked";
  return "unavailable";
}

export async function initializeActivation(
  callback: ActivationCallback,
  client: ActivationAuthClient,
  fetchImpl: typeof fetch = fetch,
): Promise<ActivationResult> {
  try {
    const exchanged = callback.kind === "session"
      ? await client.auth.setSession({ access_token: callback.accessToken, refresh_token: callback.refreshToken })
      : await client.auth.verifyOtp({ token_hash: callback.tokenHash, type: callback.type });
    if (exchanged.error || !exchanged.data.session?.access_token) return { kind: "invalid", reason: "expired" };

    const checked = await client.auth.getUser();
    if (checked.error || !validUser(checked.data.user)) return { kind: "invalid", reason: "expired" };

    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 10_000);
    let response: Response;
    try {
      response = await fetchImpl("/api/staff-activation", {
        method: "GET",
        headers: { Accept: "application/json", Authorization: `Bearer ${exchanged.data.session.access_token}` },
        signal: controller.signal,
      });
    } finally {
      window.clearTimeout(timeout);
    }
    if (!response.ok) return { kind: "invalid", reason: deniedReason(response.status) };
    const body = await response.json() as { email?: unknown; displayName?: unknown };
    if (typeof body.email !== "string" || body.email.trim().toLowerCase() !== checked.data.user.email.toLowerCase()) {
      return { kind: "invalid", reason: "subject" };
    }
    return {
      kind: "ready",
      identity: {
        email: checked.data.user.email,
        ...(typeof body.displayName === "string" && body.displayName.trim() ? { displayName: body.displayName.trim() } : {}),
      },
    };
  } catch {
    return { kind: "invalid", reason: "unavailable" };
  }
}

export class ActivationCoordinator {
  private callback: ActivationCallback | null | undefined;
  private invalidReason: ActivationInvalidReason = "missing";
  private initialization: Promise<ActivationResult> | null = null;
  private client: ActivationAuthClient | null | undefined;
  private active = false;
  private leases = 0;
  private releaseTimer: ReturnType<typeof setTimeout> | null = null;
  private generation = 0;

  capture(url: URL, scrub: () => void): ActivationCallback | null {
    if (url.pathname !== "/activate" && url.pathname !== "/activate/") return null;
    const hadCallbackMaterial = Boolean(url.search || url.hash);
    if (hadCallbackMaterial) {
      if (this.active) this.disposeAttempt();
      this.invalidReason = activationCallbackError(url);
      this.callback = consumeActivationCallback(url, scrub);
      this.active = true;
    } else if (!this.active) {
      this.callback = consumeActivationCallback(url, scrub);
      this.invalidReason = "missing";
      this.active = true;
    }
    else scrub();
    return this.callback ?? null;
  }

  retain(): () => void {
    this.leases += 1;
    if (this.releaseTimer) clearTimeout(this.releaseTimer);
    this.releaseTimer = null;
    return () => {
      this.leases = Math.max(0, this.leases - 1);
      if (this.leases === 0) {
        this.releaseTimer = setTimeout(() => {
          if (this.leases === 0) this.clear();
        }, 0);
      }
    };
  }

  acquireClient(factory: () => ActivationAuthClient | null): ActivationAuthClient | null {
    if (this.client === undefined) this.client = factory();
    return this.client;
  }

  landingState(): ActivationLandingState {
    return this.initialization ? { kind: "loading" } : this.callback ? { kind: "confirmation" } : { kind: "invalid", reason: this.invalidReason };
  }

  started(): Promise<ActivationResult> | null { return this.initialization; }

  initialize(client: ActivationAuthClient, fetchImpl: typeof fetch): Promise<ActivationResult> {
    if (this.initialization) return this.initialization;
    if (!this.callback) return Promise.resolve({ kind: "invalid", reason: this.invalidReason });
    const callback = this.callback;
    const generation = this.generation;
    this.callback = null;
    this.initialization = initializeActivation(callback, client, fetchImpl).then(async result => {
      if (generation !== this.generation || result.kind === "invalid") await client.auth.signOut({ scope: "local" }).catch(() => undefined);
      return generation === this.generation ? result : { kind: "invalid", reason: "expired" };
    });
    return this.initialization;
  }

  clear(): void {
    this.disposeAttempt();
    this.active = false;
    this.invalidReason = "missing";
  }

  private disposeAttempt(): void {
    this.generation += 1;
    if (this.client) void this.client.auth.signOut({ scope: "local" }).catch(() => undefined);
    this.callback = undefined;
    this.initialization = null;
    this.client = undefined;
    if (this.releaseTimer) clearTimeout(this.releaseTimer);
    this.releaseTimer = null;
  }
}

export const activationCoordinator = new ActivationCoordinator();
